import { useEffect, useState } from "react";
import { Plus, PencilLine, Trash } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const FREQS = { weekly: "Weekly", monthly: "Monthly", annual: "Annual", once: "Once-off" };
const EMPTY = { name: "", amount: "", frequency: "monthly", start_date: "", active: true };

export default function FixedExpenses({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/fixed-expenses").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);

  const monthlyTotal = rows.filter((r) => r.active).reduce((a, r) =>
    a + (r.frequency === "weekly" ? r.amount * 52 / 12 : r.frequency === "annual" ? r.amount / 12 : r.frequency === "monthly" ? r.amount : 0), 0);

  const submit = async () => {
    if (!form.name.trim() || !(Number(form.amount) > 0)) return toast.error("Name and positive amount required");
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount), start_date: form.start_date || null };
      if (editing) { await api.put(`/fixed-expenses/${editing.id}`, payload); toast.success("Expense updated"); }
      else { await api.post("/fixed-expenses", payload); toast.success("Expense added"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const remove = async (r) => {
    try { await api.delete(`/fixed-expenses/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e, "Delete failed")); }
  };

  return (
    <div className="space-y-6" data-testid="fixed-expenses-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {!embedded && (
          <div>
            <h1 className="font-heading text-3xl font-bold">Fixed &amp; Recurring Expenses</h1>
            <p className="text-muted-foreground mt-1">Rent, subscriptions, insurance, loan repayments — separate from invoiced supplier spend</p>
          </div>
        )}
        <Button data-testid="add-expense-btn" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-primary hover:bg-[#8B5CF6] text-white"><Plus size={16} className="mr-1" /> Add expense</Button>
      </div>

      <p className="text-sm text-muted-foreground" data-testid="expense-monthly-total">Active monthly equivalent: <span className="font-semibold text-foreground">{zar(monthlyTotal)}</span></p>

      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]" data-testid="expenses-table">
          <thead><tr className="border-b text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium text-right">Amount</th>
            <th className="px-4 py-3 font-medium">Frequency</th><th className="px-4 py-3 font-medium">Start</th>
            <th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium"></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`border-b last:border-0 ${!r.active ? "opacity-50" : ""}`} data-testid={`expense-row-${r.id}`}>
                <td className="px-4 py-2.5 font-medium">{r.name}</td>
                <td className="px-4 py-2.5 num text-right">{zar(r.amount)}</td>
                <td className="px-4 py-2.5">{FREQS[r.frequency]}</td>
                <td className="px-4 py-2.5">{r.start_date || "—"}</td>
                <td className="px-4 py-2.5"><Badge variant="outline" className={r.active ? "border-[#4A5D23]/40 text-[#A9C97E]" : "text-muted-foreground"}>{r.active ? "Active" : "Inactive"}</Badge></td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" data-testid={`expense-edit-${r.id}`} onClick={() => { setEditing(r); setForm({ name: r.name, amount: r.amount, frequency: r.frequency, start_date: r.start_date || "", active: r.active }); setOpen(true); }}><PencilLine size={15} /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive" data-testid={`expense-delete-${r.id}`}><Trash size={15} /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {r.name}?</AlertDialogTitle>
                          <AlertDialogDescription>This removes the recurring expense record.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(r)} data-testid={`expense-delete-confirm-${r.id}`} className="bg-destructive text-white hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No fixed expenses recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit expense" : "Add expense"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Fixed expense details</DialogDescription>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="expense-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Rent — premises" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Amount (R)</Label><Input type="number" step="any" min="0" data-testid="expense-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger data-testid="expense-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FREQS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Start date</Label><Input type="date" data-testid="expense-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="expense-active" /> Active</label>
            <Button onClick={submit} disabled={busy} data-testid="expense-save-btn" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">{busy ? "Saving…" : editing ? "Save changes" : "Add expense"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
